import { inject, Injectable } from '@angular/core';
import { matchAndProcessBarcode } from '@evorait/pace-components';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { BarcodeScannerInstanceToken, BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';

@Injectable()
export class ScanFnFactory {
    private readonly barcodeScanner = inject<BarcodeScannerInterface>(BarcodeScannerInstanceToken);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly log = inject<LogInterface>(LogInstanceToken);

    public textInput(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): () => Observable<string> {
        return () =>
            this.barcodeScanner.scan(pattern, appId, clipboardAccessible).pipe(
                map((result) => {
                    this.log.debug(`ScanFnFactory: text scan: ${result.text}`);
                    return matchAndProcessBarcode(result.text, pattern, result.format);
                }),
                catchError((error: unknown) => {
                    this.notify('TEXT_SCAN_ERROR_NO_TITLE', 'TEXT_SCAN_ERROR_NO_BODY', error);
                    return EMPTY;
                }),
            );
    }

    public numberInput(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): () => Observable<number | null> {
        return () =>
            this.barcodeScanner.scan(pattern, appId, clipboardAccessible).pipe(
                map((result) => {
                    this.log.debug(`ScanFnFactory: number scan: ${result.text}`);
                    const processed = matchAndProcessBarcode(result.text, pattern, result.format);
                    const parsed = ConfigConstants.validationParser.Decimal(processed);
                    return Number.isNaN(parsed) ? null : parsed;
                }),
                catchError((error: unknown) => {
                    this.notify('NUMBER_TEXT_SCAN_ERROR_NO_TITLE', 'NUMBER_TEXT_SCAN_ERROR_NO_BODY', error);
                    return EMPTY;
                }),
            );
    }

    public dropdownInput(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): () => Observable<string> {
        return () =>
            this.barcodeScanner.scan(pattern, appId, clipboardAccessible).pipe(
                map((result) => matchAndProcessBarcode(result.text, pattern, result.format)),
                catchError((error: unknown) => {
                    this.log.warn(`ScanFnFactory: dropdown scan failed: ${JSON.stringify(error)}`);
                    this.notify('DROPDOWN_TEXT_SCAN_NO_MATCH_FOUND_TITLE', 'DROPDOWN_TEXT_SCAN_ERROR_NO_BODY', error);
                    return EMPTY;
                }),
            );
    }

    public notifyDropdownNoMatch(processed: string): void {
        this.log.warn(`ScanFnFactory: no dropdown option matches '${processed}'`);
        this.notify('DROPDOWN_TEXT_SCAN_NO_MATCH_FOUND_TITLE', 'DROPDOWN_TEXT_SCAN_ERROR_NO_BODY');
    }

    private notify(titleKey: string, bodyKey: string, error?: unknown): void {
        this.appNotificationService.setAppNotification(
            AppNotificationType.ERROR,
            this.translateService.instant(titleKey) as string,
            this.translateService.instant(bodyKey, { error: JSON.stringify(error) }) as string,
        );
    }
}
